import { useState } from 'react';
import type { Provider, SessionInfo } from '@term-deck/core/types';
import { MODEL_LABELS } from '@term-deck/core/model.js';
import { CODEX_MODEL_LABELS } from '@term-deck/core/codex-model.js';
import { api } from '../api/client';
import { SessionModelControl } from './SessionModelControl';

/**
 * DD.7 (auditoria do dono, 24/09/2026) — "parece que o assistente tem um parceiro de trabalho; deixar explícito, que às
 * vezes a gente vai querer trocar o modelo desse parceiro, e estamos trabalhando cegos". O executor de um ponto é o
 * terminal do projeto: aqui aparece quem é, em que modelo e esforço, o que está fazendo, e a troca a um clique.
 */
const PROVIDER_LABEL: Record<string, string> = {
  claude: 'Claude', codex: 'Codex', glm: 'GLM 5.3 Flash', minimax: 'MiniMax', mimo: 'MiMo', 'mimo pro': 'MiMo Pro', grok: 'Grok',
};
const SWITCH_TARGETS: Provider[] = ['claude', 'codex', 'glm'];
const EFFORT_LABEL: Record<string, string> = { low: 'baixo', medium: 'médio', high: 'alto', xhigh: 'altíssimo', max: 'máximo', minimal: 'mínimo' };

export function executorSummary(session: SessionInfo): string {
  const model = session.provider === 'codex'
    ? (session.codexModel ? CODEX_MODEL_LABELS[session.codexModel as keyof typeof CODEX_MODEL_LABELS] ?? session.codexModel : null)
    : (session.model ? MODEL_LABELS[session.model as keyof typeof MODEL_LABELS] ?? session.model : null);
  const effort = session.provider === 'codex' ? session.codexEffort : session.effort;
  const activity = session.paused ? 'pausado' : session.activity === 'working' ? 'trabalhando' : session.activity === 'standby' ? 'ocioso' : 'iniciando';
  return [PROVIDER_LABEL[session.provider] ?? session.provider, model, effort ? `esforço ${EFFORT_LABEL[effort] ?? effort}` : null, activity]
    .filter(Boolean).join(' · ');
}

/** DD.17 — o executor em poucas palavras, para a linha da lista (o detalhe mostra modelo e esforço). */
export function executorShort(session: SessionInfo): string {
  const activity = session.paused ? 'pausado' : session.activity === 'working' ? 'trabalhando' : session.activity === 'standby' ? 'ocioso' : 'iniciando';
  return `${PROVIDER_LABEL[session.provider] ?? session.provider} ${activity}`;
}

/**
 * DD.7b — trocar o executor do projeto (GLM → Claude/Codex e vice-versa). Grava o provedor do projeto, o mesmo seletor de
 * Projetos; vale na próxima abertura, sem derrubar o turno em curso. A troca de modelo DENTRO de Claude/Codex continua
 * no controle da sessão logo abaixo.
 */
function ProviderSwitch({ cwd, current }: { cwd: string; current: string | null }) {
  const [pending, setPending] = useState(false), [notice, setNotice] = useState('');
  const change = async (provider: Provider) => {
    if (!provider || provider === current) return;
    setPending(true); setNotice('');
    try {
      await api.setProjectPref(cwd, provider);
      setNotice(`${PROVIDER_LABEL[provider]} passa a executar este projeto a partir da próxima abertura. O terminal atual termina o que está fazendo e fecha sozinho quando ficar ocioso.`);
    } catch (error) {
      const text = String(error);
      if (text.includes('continuity_conflict') && window.confirm('Este projeto tem trabalho em andamento em outro provedor; trocar recomeça a continuidade do zero. Trocar mesmo assim?')) {
        try { await api.setProjectPref(cwd, provider, { force: true }); setNotice(`${PROVIDER_LABEL[provider]} passa a executar este projeto a partir da próxima abertura.`); }
        catch { setNotice('Não foi possível trocar o executor. Nada mudou.'); }
      } else if (!text.includes('continuity_conflict')) setNotice('Não foi possível trocar o executor. Nada mudou.');
    } finally { setPending(false); }
  };
  return <p className="point-executor-switch">
    <label>Trocar executor{' '}
      <select aria-label="Trocar executor do projeto" value="" disabled={pending} onChange={e => void change(e.target.value as Provider)}>
        <option value="">escolher…</option>
        {SWITCH_TARGETS.filter(p => p !== current).map(p => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
      </select>
    </label>
    {notice ? <span role="status" data-testid="point-executor-switch-notice"> {notice}</span> : null}
  </p>;
}

export function PointExecutor({ project, session, cwd }: { project: string; session: SessionInfo | null | undefined; cwd?: string | null | undefined }) {
  const projectCwd = session?.cwd ?? cwd ?? null;
  // DD.17 — propriedade rotulada (como o responsável de uma tarefa): quem faz, em quê, e a troca ao lado.
  return <div className="point-executor point-prop" data-testid="point-executor">
    <span className="point-prop-label">Executor</span>
    <div className="point-prop-value">
      {session
        ? <p><strong>{project}</strong> · <span data-testid="point-executor-summary">{executorSummary(session)}</span>{' '}<a href={`/deck?focus=${encodeURIComponent(session.id)}`}>abrir terminal ↗</a></p>
        : <p><strong>{project}</strong> · terminal fechado (abre quando houver trabalho)</p>}
      <div className="point-executor-controls">
        {session ? <SessionModelControl session={session} /> : null}
        {projectCwd ? <ProviderSwitch cwd={projectCwd} current={session?.provider ?? null} /> : null}
      </div>
    </div>
  </div>;
}
